import { Fragment, type ReactNode } from 'react'
import { useAuthedBlob } from '../lib/photos'
import type { ManualImage } from '../api/types'

/**
 * Teksten i en skrevet manual (docs/DECISIONS.md beslutning 063).
 *
 * <b>Der laves aldrig HTML.</b> Teksten bliver til React-elementer, ét stykke ad
 * gangen - der er ingen `dangerouslySetInnerHTML` nogen steder. Det er derfor
 * ligegyldigt, hvad nogen skriver i en manual: skriver de en `<script>`, kommer
 * der en linje, hvor der står `<script>`. En manual er en tekst mange skal kunne
 * skrive, og den skal ikke kunne køre noget i en kollegas browser.
 *
 * Syntaksen er med vilje lille - den skal kunne huskes af en, der skriver to
 * manualer om året:
 *
 * ```
 * # Overskrift          ## Underoverskrift     ### Mindre overskrift
 * - punkt               1. nummereret punkt
 * **fed**               *kursiv*
 * > husk det her        (fremhævet boks)
 * ![Tavlen set forfra](billede:12)
 * ```
 *
 * Billeder refereres som `billede:{id}` og aldrig som en URL: manualen må ikke
 * hente noget udefra, og et billede, der forsvinder, fordi en leverandør lagde
 * sit website om, er en manual med et hul i.
 */
export function ManualText({
  content,
  manualId,
  images
}: {
  content: string
  manualId: number
  images: ManualImage[]
}) {
  const blocks = parseBlocks(content)

  return (
    <div className="manual-text">
      {blocks.map((block, index) => (
        <Block key={index} block={block} manualId={manualId} images={images} />
      ))}
    </div>
  )
}

// ---------- Blokke ----------

type Block =
  | { type: 'heading'; level: 1 | 2 | 3; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'quote'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'image'; alt: string; imageId: number }

/**
 * Deler teksten op i blokke. Linjebaseret, fordi det er sådan folk skriver:
 * en tom linje er et nyt afsnit, og et streg-punkt er et punkt.
 */
function parseBlocks(content: string): Block[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []

  let paragraph: string[] = []

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
      paragraph = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed === '') {
      flush()
      continue
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(trimmed)
    if (heading) {
      flush()
      blocks.push({
        type: 'heading',
        level: heading[1].length as 1 | 2 | 3,
        text: heading[2].trim()
      })
      continue
    }

    const image = /^!\[([^\]]*)\]\(billede:(\d+)\)$/.exec(trimmed)
    if (image) {
      flush()
      blocks.push({ type: 'image', alt: image[1], imageId: Number(image[2]) })
      continue
    }

    if (trimmed.startsWith('> ')) {
      flush()
      // Sammenhaengende citatlinjer bliver til én boks.
      const parts: string[] = []
      let j = i
      while (j < lines.length && lines[j].trim().startsWith('> ')) {
        parts.push(lines[j].trim().slice(2).trim())
        j++
      }
      blocks.push({ type: 'quote', text: parts.join(' ') })
      i = j - 1
      continue
    }

    const bullet = /^[-*]\s+(.*)$/.exec(trimmed)
    const numbered = /^\d+[.)]\s+(.*)$/.exec(trimmed)

    if (bullet || numbered) {
      flush()
      const ordered = Boolean(numbered)
      const items: string[] = []
      let j = i

      while (j < lines.length) {
        const candidate = lines[j].trim()
        const asBullet = /^[-*]\s+(.*)$/.exec(candidate)
        const asNumbered = /^\d+[.)]\s+(.*)$/.exec(candidate)
        const match = ordered ? asNumbered : asBullet

        if (!match) break

        items.push(match[1].trim())
        j++
      }

      blocks.push({ type: 'list', ordered, items })
      i = j - 1
      continue
    }

    paragraph.push(trimmed)
  }

  flush()
  return blocks
}

function Block({
  block,
  manualId,
  images
}: {
  block: Block
  manualId: number
  images: ManualImage[]
}) {
  switch (block.type) {
    case 'heading': {
      const Tag = (['h2', 'h3', 'h4'] as const)[block.level - 1]
      return <Tag className="manual-text__heading">{inline(block.text)}</Tag>
    }

    case 'list':
      return block.ordered ? (
        <ol className="manual-text__list">
          {block.items.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ol>
      ) : (
        <ul className="manual-text__list">
          {block.items.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ul>
      )

    case 'quote':
      return <div className="manual-text__quote">{inline(block.text)}</div>

    case 'image':
      return <ManualFigure manualId={manualId} block={block} images={images} />

    case 'paragraph':
      return <p className="manual-text__paragraph">{inline(block.text)}</p>
  }
}

/**
 * Et billede fra manualen. Findes id-et ikke længere - billedet er slettet, men
 * teksten henviser stadig til det - <b>siges det</b>. En tom plads ville se ud,
 * som om der ikke skulle stå noget.
 */
function ManualFigure({
  manualId,
  block,
  images
}: {
  manualId: number
  block: { alt: string; imageId: number }
  images: ManualImage[]
}) {
  const image = images.find((i) => i.id === block.imageId)
  const url = useAuthedBlob(image ? `/api/manuals/${manualId}/images/${image.id}` : '')

  if (!image) {
    return (
      <div className="manual-text__missing">
        Her stod et billede, der er slettet {block.alt ? `("${block.alt}")` : ''}. Ret manualen, så
        henvisningen forsvinder.
      </div>
    )
  }

  return (
    <figure className="manual-text__figure">
      {url ? (
        <img src={url} alt={block.alt || image.name} />
      ) : (
        <div className="manual-text__placeholder">Henter billede ...</div>
      )}
      {block.alt && <figcaption>{block.alt}</figcaption>}
    </figure>
  )
}

// ---------- Inline ----------

/**
 * **fed** og *kursiv* inde i en linje. Alt andet står, som det er skrevet -
 * også vinkelparenteser, for teksten bliver aldrig til HTML.
 */
function inline(text: string): ReactNode {
  const parts: ReactNode[] = []
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/g

  let last = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index))
    }

    const token = match[0]
    if (token.startsWith('**')) {
      parts.push(<strong key={match.index}>{token.slice(2, -2)}</strong>)
    } else {
      parts.push(<em key={match.index}>{token.slice(1, -1)}</em>)
    }

    last = match.index + token.length
  }

  if (last < text.length) {
    parts.push(text.slice(last))
  }

  return parts.map((part, index) => <Fragment key={index}>{part}</Fragment>)
}
